import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionIcon, Badge, Button, Card, Group, Skeleton, Stack, Text } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconBuildingBank, IconPlus, IconTrash } from '@tabler/icons-react';
import AddBankDialog from '@/components/app/company/AddBankDialog';
import EmptyState from '@/components/common/EmptyState';
import { CopyIconButton } from '@/components/common/CopyIconButton';
import { useDeepLinkAction } from '@/hooks/useDeepLinkAction';
import { companyService } from '@/api/services/CompanyService';
import { getApiMessage, isNetworkError } from '@/api/errors';
import { BANK_DETAILS_QUERY_KEY, useBankDetails } from '@/hooks/useBankDetails';
import { useIsManager } from '@/hooks/useRoles';
import { REQUIRED_DATA_QUERY_KEY } from '@/hooks/useRequiredData';
import type { BankAccount } from '@/types/company';
import IdText from '@/components/common/IdText';

// Реквизиты счёта одним блоком «подпись: значение» — для копирования целиком.
function accountToText(account: BankAccount, t: (key: string) => string): string {
  return [
    account.name,
    `${t('app.company.banking.rs')}: ${account.rs}`,
    `${t('app.company.banking.bik')}: ${account.bik}`,
    account.ks ? `${t('app.company.banking.ks')}: ${account.ks}` : null,
  ]
    .filter(Boolean)
    .join('\n');
}

// Гарантируем ровно один основной счёт: если он один в списке — он основной; иначе сохраняем выбор.
function withSingleMain(accounts: BankAccount[]): BankAccount[] {
  if (accounts.length === 0) {
    return accounts;
  }
  if (!accounts.some((account) => account.isMain)) {
    return accounts.map((account, index) => ({ ...account, isMain: index === 0 }));
  }
  return accounts;
}

// G2 — Банковские реквизиты: список счетов + добавление (БИК-flow) + основной/удаление.
export default function BankingTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // Реквизиты — орг-данные: добавлять/менять/удалять счета может только владелец-менеджер (бэкенд отдаёт
  // 403 остальным). Сотрудник видит счета организации, но не редактирует; копирование доступно всем.
  const canEdit = useIsManager();
  const { data: accounts, isLoading, isError } = useBankDetails();
  const [dialogOpened, setDialogOpened] = useState(false);

  // Переход из баннера обязательных данных: ?createNew=true — сразу открыть добавление.
  useDeepLinkAction('createNew', () => setDialogOpened(true));

  const mutation = useMutation({
    mutationFn: (details: BankAccount[]) => companyService.saveBankDetails(details),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BANK_DETAILS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: REQUIRED_DATA_QUERY_KEY });
      notifications.show({ color: 'lime', message: t('app.profile.saved') });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        message: isNetworkError(error)
          ? t('auth.errors.generic')
          : (getApiMessage(error) ?? t('auth.errors.generic')),
      });
    },
  });

  function handleAdd(account: BankAccount): void {
    const current = accounts ?? [];
    const next = account.isMain
      ? [...current.map((item) => ({ ...item, isMain: false })), account]
      : [...current, account];
    mutation.mutate(withSingleMain(next));
    setDialogOpened(false);
  }

  function handleSetMain(index: number): void {
    const next = (accounts ?? []).map((item, i) => ({ ...item, isMain: i === index }));
    mutation.mutate(next);
  }

  function confirmDelete(index: number): void {
    // Бэкенд запрещает удалять единственный счёт (вернёт ошибку) — не шлём заведомо отклоняемый
    // запрос, сразу объясняем. ensureSuccess в сервисе остаётся подстраховкой.
    if ((accounts ?? []).length === 1) {
      notifications.show({ color: 'wheat', message: t('app.company.banking.cantDeleteLast') });
      return;
    }
    modals.openConfirmModal({
      title: t('app.company.banking.deleteTitle'),
      children: <Text size="sm">{t('app.company.banking.deleteConfirm')}</Text>,
      labels: { confirm: t('app.company.banking.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        const next = withSingleMain((accounts ?? []).filter((_, i) => i !== index));
        mutation.mutate(next);
      },
    });
  }

  if (isError) {
    return (
      <Card withBorder padding="lg">
        <Text c="red">{t('auth.errors.generic')}</Text>
      </Card>
    );
  }

  return (
    <Stack gap="md">
      {canEdit && (accounts ?? []).length > 0 ? (
        <Group justify="flex-end">
          <Button leftSection={<IconPlus size={16} />} onClick={() => setDialogOpened(true)}>
            {t('app.company.banking.add')}
          </Button>
        </Group>
      ) : null}

      {isLoading ? (
        <>
          <Skeleton h={88} />
          <Skeleton h={88} />
        </>
      ) : (accounts ?? []).length === 0 ? (
        <Card withBorder padding="lg">
          <EmptyState
            decorated={false}
            icon={<IconBuildingBank size={30} />}
            title={t('app.company.banking.empty')}
            description={t('app.company.banking.emptyHint')}
            action={
              canEdit ? (
                <Button leftSection={<IconPlus size={16} />} onClick={() => setDialogOpened(true)}>
                  {t('app.company.banking.add')}
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : (
        (accounts ?? []).map((account, index) => (
          <Card key={account.id ?? `${account.bik}-${account.rs}`} withBorder padding="md">
            {/* Единый стиль с карточками «Аттестации»/«Агентского договора»: бейдж (основной /
                не основной) — в правом верхнем углу, действия (сделать основным, копировать, удалить)
                — в правом нижнем. align=stretch + justify=space-between разносят их по углам. */}
            <Group justify="space-between" gap="sm" align="stretch" wrap="nowrap">
              <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                <Text fw={600}>{account.name ?? <IdText>{account.bik}</IdText>}</Text>
                <Text size="sm" c="gray.7">
                  {t('app.company.banking.rs')}: <IdText>{account.rs}</IdText>
                </Text>
                <Text size="sm" c="gray.7">
                  {t('app.company.banking.bik')}: <IdText>{account.bik}</IdText>
                  {account.ks ? (
                    <>
                      {` · ${t('app.company.banking.ks')}: `}
                      <IdText>{account.ks}</IdText>
                    </>
                  ) : null}
                </Text>
              </Stack>
              <Stack gap="xs" align="flex-end" justify="space-between">
                <Badge
                  color={account.isMain ? 'brand' : 'gray'}
                  variant="light"
                  style={{ flexShrink: 0 }}
                >
                  {account.isMain
                    ? t('app.company.banking.mainBadge')
                    : t('app.company.banking.notMainBadge')}
                </Badge>
                <Group gap={8} wrap="nowrap">
                  {canEdit && !account.isMain ? (
                    <Button
                      variant="subtle"
                      size="compact-sm"
                      onClick={() => handleSetMain(index)}
                      disabled={mutation.isPending}
                    >
                      {t('app.company.banking.setMain')}
                    </Button>
                  ) : null}
                  <CopyIconButton
                    value={accountToText(account, t)}
                    ariaLabel={t('app.company.banking.copyAccount')}
                  />
                  {canEdit ? (
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      aria-label={t('app.company.banking.delete')}
                      onClick={() => confirmDelete(index)}
                      disabled={mutation.isPending}
                    >
                      <IconTrash size={18} />
                    </ActionIcon>
                  ) : null}
                </Group>
              </Stack>
            </Group>
          </Card>
        ))
      )}

      <AddBankDialog
        opened={dialogOpened}
        existing={accounts ?? []}
        onAdd={handleAdd}
        onClose={() => setDialogOpened(false)}
      />
    </Stack>
  );
}
